import { Helmet } from 'react-helmet-async';
import React, { useState } from 'react';
import { motion} from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const ChemicalPeels = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const peelTypes = [
    {
      title: 'VI Chemical Peel (Face and Neck)',
      price: '$300'
    },
    {
      title: 'VI Chemical Peel (Body)',
      price: '$300'
    }
  ];

  const benefits = [
    {
      title: 'Skin Rejuvenation',
      description: 'Chemical peels promote cell turnover and stimulate collagen production, resulting in smoother, more youthful-looking skin.'
    },
    {
      title: 'Improves Texture',
      description: 'Reduces rough texture and refines skin surface for a more even complexion and improved skin tone.'
    },
    {
      title: 'Reduces Hyperpigmentation',
      description: 'Effectively treats sun damage, age spots, melasma, and post-inflammatory hyperpigmentation.'
    },
    {
      title: 'Treats Multiple Concerns',
      description: 'Addresses various skin issues including fine lines, acne, scarring, and uneven skin tone in one treatment.'
    },
    {
      title: 'Minimal Downtime',
      description: 'Most chemical peels require minimal recovery time, with peeling typically lasting 3-7 days.'
    },
    {
      title: 'Safe for Most Skin Types',
      description: 'VI Peels are formulated to be safe and effective for all skin types and tones.'
    },
    {
      title: 'Long-Lasting Results',
      description: 'Results continue to improve over several months as collagen production increases and skin regenerates.'
    },
    {
      title: 'Enhances Product Absorption',
      description: 'After peeling, skin is more receptive to skincare products, maximizing their effectiveness.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Chemical Peels for Skin Rejuvenation | Century Aesthetics Forest Hills</title>
        <meta name="description" content="Professional chemical peels for acne, wrinkles, and hyperpigmentation. Customized treatments for all skin types in Forest Hills, NY." />
        <meta name="keywords" content="chemical peel, skin peel, acne treatment, anti-aging peel, chemical exfoliation, skin rejuvenation" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/chemical-peels" />
        
        <meta property="og:title" content="Chemical Peels | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Professional chemical peels for acne, wrinkles, and hyperpigmentation. Customized treatments for all skin types in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/chemical-peels" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">

      
      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/chemical.jpg" 
                  alt="Chemical Peels Treatment" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                REVITALIZE DULL SKIN WITH ADVANCED FACIAL PEELS
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Chemical peels use specialized acids and enzymes to exfoliate the skin, removing dead skin cells and promoting regeneration. The result? Smoother, brighter, and more youthful skin as a result of the application of a chemical solution is a hydrate. We now regenerated skin is more smooth and less wrinkled than the old. Chemical peels can treat various areas including the face, neck, and hands, addressing issues such as aging spots, fine lines, and sun damage.
                </p>
                <p>
                  Chemical peels differ depending on the ingredients, strength, and the results they provide. They can be classified into three main types: superficial, medium, and deep. Superficial peels use mild acids to gently exfoliate the outer layer of skin and the mild ingredients in this type light most peels begin to see improvements as soon as six skin heals, typically within 1-2 weeks. Results can last anywhere from one to several months, depending on the depth of the peel and the skin's maintenance post-treatment. If you are interested in achieving a fresher, younger look, book an appointment with Century Medical Services in Forest Hills, NY for an assessment if this treatment is right for you.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Peel Types Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Chemical Peel Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {peelTypes.map((peel, index) => (
              <motion.div
                key={peel.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg p-12 shadow-2xl"
              >
                <div className="text-center">
                  {/* Elegant Icon */}
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gold/10 flex items-center justify-center">
                    <svg 
                      className="w-12 h-12 text-gold" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" 
                      />
                    </svg>
                  </div>
                  
                  <h3 className="text-2xl font-heading font-normal text-olive-darkest mb-3 tracking-wider">
                    {peel.title}
                  </h3>
                  <p className="text-6xl font-heading font-light text-gold mb-2 tracking-tight">{peel.price}</p>
                  <p className="text-lg font-body text-gray-600 mb-8">per treatment</p>
                  
                  <motion.button
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={scrollToContact}
                    className="w-full bg-olive-darkest text-white px-8 py-4 rounded-lg font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Book Now
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Free Consultation Notice */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-3 bg-gold/10 border-2 border-gold px-8 py-4 rounded-lg">
              <CheckCircle className="w-6 h-6 text-gold" />
              <p className="text-white text-lg font-medium">
                Complimentary Consultation Included
              </p>
            </div>
          </motion.div> */}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Chemical Peels</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          {/* Carousel Container */}
          <div className="relative max-w-5xl mx-auto px-16">
            {/* Navigation Buttons - Positioned Outside */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentSlide((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-olive-darkest text-white p-3 rounded-full shadow-lg hover:bg-gold hover:text-olive-darkest transition-all duration-300 z-10"
              aria-label="Previous benefit"
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentSlide((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-olive-darkest text-white p-3 rounded-full shadow-lg hover:bg-gold hover:text-olive-darkest transition-all duration-300 z-10"
              aria-label="Next benefit"
            >
              <ChevronRight className="w-6 h-6" />
            </motion.button>

            {/* Carousel Items */}
            <div className="overflow-hidden">
              <motion.div
                className="flex"
                animate={{
                  x: `-${currentSlide * 100}%`
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30
                }}
              >
                {benefits.map((benefit, index) => (
                  <div key={benefit.title} className="min-w-full px-4">
                    <BenefitCard benefit={benefit} index={index} />
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {benefits.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    currentSlide === index
                      ? 'w-8 h-3 bg-olive-darkest'
                      : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Why Choose Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Why Choose Century Medical Services for Chemical Peels?</h2>
              <div className="w-20 h-1 bg-gold mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[
                {
                  title: 'Expert Assessment',
                  description: 'Our medical professionals evaluate your skin type and concerns to recommend the most appropriate peel for your needs.'
                },
                {
                  title: 'Premium Products',
                  description: 'We use only high-quality VI Peel products that are proven safe and effective for all skin types.'
                },
                {
                  title: 'Personalized Treatment',
                  description: 'Each chemical peel treatment is customized to address your specific skin concerns and goals.'
                },
                {
                  title: 'Comprehensive Aftercare',
                  description: 'We provide detailed post-treatment care instructions to ensure optimal results and minimize downtime.'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex gap-4 items-start"
                >
                  <CheckCircle className="w-6 h-6 text-olive-darkest flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-heading font-bold text-olive-darkest mb-2 text-lg">{item.title}</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-10 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
            >
              <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
                Transform your skin with professional chemical peel treatments. Experience smoother, brighter, and more youthful-looking skin today!
              </p>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
              >
                Schedule Consultation
              </motion.button>

              {/* Divider */}
              <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

              {/* Have Questions Section */}
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
              <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
                Learn about chemical peel treatments, preparation requirements, and what to expect during and after your appointment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <motion.button
                  whileHover={{ y: -2, scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('chemical-peels');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                  className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
                >
                  View FAQs
                </motion.button>
                <motion.button
                  whileHover={{ y: -2, scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContact}
                  className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
                >
                  Contact Us Directly
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

// BenefitCard Component
const BenefitCard = ({ benefit, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.05 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="relative bg-white rounded-lg shadow-md overflow-hidden cursor-pointer group"
    >
      {/* Background color overlay */}
      <motion.div
        initial={{ height: 0 }}
        animate={{ height: isHovered ? '100%' : 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        className="absolute bottom-0 left-0 w-full bg-olive-darkest z-0"
      />

      {/* Content */}
      <div className="relative z-10 p-6">
        <motion.div
          animate={{ 
            color: isHovered ? '#B8956A' : '#4A4D3A'
          }}
          transition={{ duration: 0.4 }}
          className="mb-4"
        >
          <CheckCircle className="w-12 h-12" strokeWidth={1.5} />
        </motion.div>

        <motion.h3
          animate={{ 
            color: isHovered ? '#FFFFFF' : '#4A4D3A'
          }}
          transition={{ duration: 0.4 }}
          className="font-heading text-2xl font-semibold mb-3"
        >
          {benefit.title}
        </motion.h3>

        <motion.p
          animate={{ 
            color: isHovered ? '#FFFFFF' : '#374151',
            opacity: isHovered ? 0.95 : 0.8
          }}
          transition={{ duration: 0.4 }}
          className="text-sm leading-relaxed"
        >
          {benefit.description}
        </motion.p>
      </div>

      {/* Border effect */}
      <motion.div
        animate={{ 
          borderTopColor: isHovered ? '#B8956A' : 'transparent'
        }}
        transition={{ duration: 0.4 }}
        className="absolute top-0 left-0 w-full h-1 border-t-4"
      />
    </motion.div>
  );
};

export default ChemicalPeels;